/**
 * State Detail Editor
 * 
 * Reusable component for editing state details
 * Can be used in tabs, cards, or dialogs
 */

import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Card, message, Button, Space, Checkbox } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { apiClient } from '../../../services/apiClient';
import { DemandType, DemandFlowType } from '../../../types/enums';
import './EditorStyles.css';

const { TextArea } = Input;

interface StateDetailEditorProps {
  stateId: string;
  artifactId: string;
  onSave?: () => void;
  embedded?: boolean; // If true, no save button (parent handles save)
  onChange?: (values: any) => void; // Callback for embedded mode
}

export const StateDetailEditor: React.FC<StateDetailEditorProps> = ({
  stateId,
  artifactId,
  onSave,
  embedded = false,
  onChange
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stateData, setStateData] = useState<any>(null);

  useEffect(() => {
    loadStateData();
  }, [stateId, artifactId]);

  // Set form values when stateData is loaded
  useEffect(() => {
    if (stateData && !loading) {
      form.setFieldsValue({
        name: stateData.name,
        description: stateData.description || '',
        state_type: stateData.state_type,
        demand_type: stateData.demand_type || DemandType.SUPPLY,
        demand_flow_type: stateData.demand_flow_type || DemandFlowType.PULL,
        is_initial: stateData.is_initial || false,
        is_final: stateData.is_final || false,
        is_required: stateData.is_required || false,
        is_improvement: stateData.is_improvement || false,
      });
    }
  }, [stateData, loading, form]);

  const loadStateData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/prime-artifacts/${artifactId}/states/${stateId}/view`);
      setStateData(response.data);
    } catch (error) {
      console.error('Error loading state data:', error);
      message.error('Failed to load state data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        name: values.name,
        description: values.description,
        state_type: values.state_type,
        demand_type: values.demand_type,
        demand_flow_type: values.demand_flow_type,
        is_initial: values.is_initial,
        is_final: values.is_final,
        is_required: values.is_required,
        is_improvement: values.is_improvement || false,
        prime_artifact_ref: artifactId,
      };

      await apiClient.put(`/prime-artifacts/${artifactId}/states/${stateId}`, payload);
      message.success('State updated successfully');
      onSave?.();
    } catch (error: any) {
      console.error('Error saving state:', error);
      message.error(`Failed to save state: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = () => {
    if (embedded && onChange) {
      const values = form.getFieldsValue();
      onChange(values);
    }
  };

  if (loading) {
    return <div className="editor-loading">Loading state data...</div>;
  }

  return (
    <div className="state-detail-editor">
      <Card
        title={`State: ${stateData?.name || 'Unknown'}`}
        className="editor-card"
        extra={
          !embedded && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              Save Changes
            </Button>
          )
        }
      >
        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleFormChange}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please enter a state name' }]}
          >
            <Input placeholder="Enter state name" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
          >
            <TextArea rows={3} placeholder="Enter state description" />
          </Form.Item>

          <Form.Item
            label="State Type"
            name="state_type"
            rules={[{ required: true, message: 'Please select a state type' }]}
          >
            <Select placeholder="Select state type">
              <Select.Option value="CREATION">Creation</Select.Option>
              <Select.Option value="ACTIVE">Active</Select.Option>
              <Select.Option value="CONSUMPTION">Consumption</Select.Option>
              <Select.Option value="INACTIVE">Inactive</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Demand Type"
            name="demand_type"
          >
            <Select placeholder="Select demand type">
              <Select.Option value="SUPPLY">Supply</Select.Option>
              <Select.Option value="DEMAND">Demand</Select.Option>
              <Select.Option value="TARGET">Target</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Demand Flow Type"
            name="demand_flow_type"
          >
            <Select placeholder="Select demand flow type">
              <Select.Option value="PUSH">Push</Select.Option>
              <Select.Option value="PULL">Pull</Select.Option>
              <Select.Option value="DRUM">Drum</Select.Option>
            </Select>
          </Form.Item>

          <Space direction="horizontal" size="large" style={{ marginTop: 8 }}>
            <Form.Item
              name="is_initial"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Checkbox>Initial State</Checkbox>
            </Form.Item>

            <Form.Item
              name="is_final"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Checkbox>Final State</Checkbox>
            </Form.Item>

            <Form.Item
              name="is_required"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Checkbox>Required</Checkbox>
            </Form.Item>

            <Form.Item
              name="is_improvement"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Checkbox>Improvement State</Checkbox>
            </Form.Item>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

